import type { RuntimeFormResponse, RuntimeNavigationPublication, RuntimeFolder } from '../api/types'

export type StaticManifest = {
  schema_version: number; workspace_id: string; member_id: string; mode: 'static' | 'bitrix_client' | 'server' | 'blocked'
  folders?: RuntimeFolder[]; ttl_seconds: number; generation: string; forms: Array<RuntimeNavigationPublication & { url: string; revision: number }>; reasons: unknown[]
}
type CachedManifest = { expires: number; data: StaticManifest }
const base = (import.meta.env.VITE_RUNTIME_BASE_URL || '/runtime').replace(/\/$/, '')
export function runtimeRoot(key: string) {
  if (!/^[A-Za-z0-9_-]{32,64}$/.test(key)) throw new Error('Некорректный ключ подключения')
  return `${base}/${key}`
}
export async function loadManifest(key: string, member: string, refresh = false): Promise<CachedManifest> {
  const cacheKey = `sas_manifest:${member}:${key}`
  try {
    const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null') as CachedManifest | null
    if (!refresh && cached && cached.expires > Date.now() && cached.expires - Date.now() <= 300000 && cached.data.member_id === member) return cached
  } catch { sessionStorage.removeItem(cacheKey) }
  // Revalidate at the TTL boundary: repeatedly opening the app must not prolong old rights.
  const response = await fetch(`${runtimeRoot(key)}/manifest.json`, { cache: refresh ? 'reload' : 'default' })
  if (!response.ok) throw new Error('Опубликованные формы ещё не подготовлены. Обновите приложение после публикации.')
  const data = await response.json() as StaticManifest
  if (data.schema_version !== 1 || data.member_id !== member || !Array.isArray(data.forms)) throw new Error('Подключение принадлежит другому порталу или manifest повреждён')
  if (!refresh && (data.ttl_seconds > 300 || ['server', 'bitrix_client'].includes(data.mode))) return loadManifest(key, member, true)
  const ttl = Math.min(Math.max(data.ttl_seconds, 60), 300)
  const generated = Date.parse(response.headers.get('date') || '')
  const age = Math.max(Number(response.headers.get('age') || 0), Number.isFinite(generated) ? (Date.now() - generated) / 1000 : 0)
  const cached = { data, expires: Date.now() + Math.max(ttl - age, 0) * 1000 }
  sessionStorage.setItem(cacheKey, JSON.stringify(cached))
  return cached
}
export async function loadStaticForm(key: string, manifest: StaticManifest, publicKey: string): Promise<RuntimeFormResponse> {
  const item = manifest.forms.find(form => form.public_key === publicKey)
  if (!item || !['static', 'bitrix_client'].includes(manifest.mode)) throw new Error('Форма недоступна на текущем тарифе')
  if (!/^bundles\/[a-f0-9]{64}\.json$/.test(item.url)) throw new Error('Некорректная версия формы')
  const response = await fetch(`${runtimeRoot(key)}/${item.url}`)
  if (!response.ok) throw new Error('Не удалось загрузить опубликованную версию')
  return await response.json() as RuntimeFormResponse
}
